import { useSeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, optimizeImg } from "../lib/api";
import { ArrowRight, Dna, GitBranch, Users } from "lucide-react";

const STATUS_COLORS = {
  Planning: "border-white/20 text-qbfg/70",
  Foundation: "border-sky-400/40 text-sky-300",
  Pairing: "border-amber-400/40 text-amber-300",
  Breeding: "border-amber-400/40 text-amber-300",
  "Ovulation Observed": "border-orange-400/40 text-orange-300",
  Gravid: "border-orange-400/40 text-orange-300",
  "Litter Born": "border-emerald-400/40 text-emerald-300",
  Evaluation: "border-emerald-400/40 text-emerald-300",
  "Holdback Selection": "border-violet-400/40 text-violet-300",
  "Growing Holdbacks": "border-violet-400/40 text-violet-300",
  "Next Generation": "border-qbprimary/50 text-qbprimary",
  Ongoing: "border-qbprimary/50 text-qbprimary",
};

export const StatusPill = ({ status }) => (
  <span data-testid="project-status" className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border bg-qbbg/70 backdrop-blur ${STATUS_COLORS[status] || STATUS_COLORS.Planning}`}>
    <GitBranch className="w-3 h-3" /> {status}
  </span>
);

const AnimalChip = ({ a }) => {
  const nav = useNavigate();
  return (
    <span role="link" tabIndex={0} data-testid={`project-animal-${a.boa.id}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); nav(`/boa/${a.boa.id}`); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); nav(`/boa/${a.boa.id}`); } }}
      className="group/chip flex items-center gap-2 pr-3 rounded-full border border-white/10 bg-qbbg/60 hover:border-qbprimary/50 transition-colors cursor-pointer">
      <span className="w-8 h-8 rounded-full overflow-hidden bg-qbsurface shrink-0">
        {a.boa.images?.[0] && <img src={optimizeImg(a.boa.images[0], 200)} alt={a.boa.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
      </span>
      <span className="text-xs text-qbfg group-hover/chip:text-qbprimary transition-colors whitespace-nowrap">{a.boa.name}</span>
    </span>
  );
};

export default function Projects() {
  useSeo({
    title: "Breeding Projects | Quantum Boas",
    description: "Multi-generation boa breeding projects at Quantum Boas — the genetics, the reasoning and the selection behind every animal, documented as living project journals.",
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-28 px-6 md:px-10" data-testid="projects-page">
      <div className="max-w-[1200px] mx-auto">
        <span className="text-xs tracking-[0.28em] uppercase text-qbprimary font-semibold">The breeding program</span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl mt-4 mb-5 leading-[1.02] pb-1">
          Breeding <span className="italic watercolor-text">projects</span>
        </h1>
        <p className="text-qbfg/60 max-w-xl text-base">
          Multi-generation projects, documented as they unfold — the genetics, the reasoning, and the selection behind every animal we keep.
        </p>
        <p className="font-display italic text-lg md:text-xl text-qbfg/80 mt-6 mb-16 border-l-2 border-qbprimary/60 pl-4" data-testid="projects-philosophy">
          “Genetics tell us what is possible. Selection determines where we take it.”
        </p>

        {loading ? (
          <p className="text-qbfg/40 py-20 text-center">Loading projects…</p>
        ) : projects.length === 0 ? (
          <p className="text-qbfg/40 py-20 text-center" data-testid="no-projects">No active projects right now — check back soon.</p>
        ) : (
          <div className="space-y-8 md:space-y-12" data-testid="projects-list">
            {projects.map((p, i) => {
              const hero = p.hero_image || p.animals?.find((a) => a.boa?.images?.[0])?.boa.images[0];
              const genetics = p.genetics?.length
                ? p.genetics
                : [...new Set(p.animals.flatMap((a) => a.boa?.morphs || []))].slice(0, 8);
              return (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: Math.min(i, 2) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="border border-white/10 rounded-2xl overflow-hidden bg-qbsurface hover:border-qbprimary/30 transition-colors"
                >
                  <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="group grid md:grid-cols-2">
                    <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[340px] overflow-hidden bg-qbbg">
                      {hero ? (
                        <img src={optimizeImg(hero, 1000)} alt={p.title} loading="lazy" decoding="async"
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-qbfg/20"><GitBranch className="w-12 h-12" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-qbbg/80 via-transparent to-transparent md:bg-gradient-to-r" />
                      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        <StatusPill status={p.status} />
                        {p.generation && <span className="text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border border-white/15 text-qbfg/70 bg-qbbg/70 backdrop-blur">{p.generation}</span>}
                      </div>
                    </div>
                    <div className="p-6 md:p-10 flex flex-col">
                      <h2 className="font-display text-3xl md:text-4xl leading-[1.05] pb-0.5 group-hover:text-qbprimary transition-colors">{p.title}</h2>
                      {p.subtitle && <p className="watercolor-text italic mt-1">{p.subtitle}</p>}
                      {p.thesis && <p className="text-qbfg/65 mt-4 leading-relaxed text-sm md:text-base">{p.thesis}</p>}
                      {genetics.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-5">
                          <Dna className="w-3.5 h-3.5 text-qbprimary mt-1" />
                          {genetics.map((g) => <span key={g} className="text-[10px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-qbfg/70">{g}</span>)}
                        </div>
                      )}
                      {p.animals.length > 0 && (
                        <div className="mt-5">
                          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-qbfg/40 mb-2.5">
                            <Users className="w-3 h-3" /> Foundation animals
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {p.animals.map((a) => <AnimalChip key={a.boa.id} a={a} />)}
                          </div>
                        </div>
                      )}
                      <span className="mt-auto pt-7 inline-flex items-center gap-2 text-sm text-qbprimary">
                        Open project journal <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
